import { CategoryTypeEnum } from '@repo/common/enums';
import {
  OneStoreAddressAttributesInterface,
  OneStoreShopAttributes,
  OneStoreWorkingHourAttributesInterface,
} from './one-store.interface';
import { SlotInterface } from './time-slot.interface';
import { Meeting } from './meeting.interface.ts';

export interface BookingStatus {
  service?: BookingCountryService;
  shop?: BookingShop;
  timeSlot?: SlotInterface;
}

export interface BookingProductSheet {
  sku: string;
  locale: string;
  name: string;
  description: string | null;
  image: string | null;
  category: CategoryTypeEnum;
  duration: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  countryService?: BookingCountryService;
  service?: BookingService;
}

export enum BookingServiceStatusEnum {
  LIVE = 'LIVE',
  DISABLED = 'DISABLED',
}

export enum BookingServiceVisibilityEnum {
  PRIVATE = 'PRIVATE',
  PUBLIC = 'PUBLIC',
  UNLISTED = 'UNLISTED',
}

export interface BookingServiceDependencyOption {
  skuId: string;
  quantity: number;
}

export interface BookingServiceDependency {
  resourceOptions: BookingServiceDependencyOption[];
}

export interface BookingService {
  sku: string;
  shopId: string;
  duration: number;
  code: string;
  resourceDependencies: BookingServiceDependency[];
  optionId: string;
  visibility: BookingServiceVisibilityEnum;
  status: BookingServiceStatusEnum;
  bookingRules: {
    startTimeIntervalSec: number;
    extraTimeAfterSec: number;
    minimalDelaySec: number;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  productSheet?: BookingProductSheet;
  context: BookingShopContextEnum;
  remoteCountries?: string[];
  modality?: BookingServiceModalityEnum;
  subModality?: BookingServiceSubModalityEnum;
}

export type CreateBookingServicePayload = Omit<
  BookingService,
  'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'visibility' | 'optionId'
>;

export type UpdateBookingServicePayload = Omit<
  BookingService,
  'id' | 'createdAt' | 'updatedAt' | 'deletedAt'
>;

export interface BookingCountryServiceOffer {
  amount?: number;
  currency?: string;
}

export enum BookingServiceModalityEnum {
  ON_SITE = 'ON_SITE',
  REMOTE = 'REMOTE',
}

export enum BookingServiceSubModalityEnum {
  VIDEO_CALL = 'VIDEO_CALL',
  PHONE_CALL = 'PHONE_CALL',
}

export interface BookingCountryService {
  country: string;
  sku: string;
  models: string[];
  offer: BookingCountryServiceOffer;
  productSheet?: BookingProductSheet;
  duration: number;
  service?: BookingService;
  bookingRules: {
    startTimeIntervalSec: number;
    extraTimeAfterSec: number;
    minimalDelaySec: number;
  };
  modality: BookingServiceModalityEnum;
  subModality?: BookingServiceSubModalityEnum;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface BookingCountryServiceV2 extends BookingCountryService {
  oneCatalogSku: string;
  category: CategoryTypeEnum;
}

export interface BookingShopWorkingHours {
  dayOfWeek: number;
  timeIntervals: { start: string; end: string }[];
}

export enum BookingShopContextEnum {
  STORE = 'STORE',
  VISIO_STORE = 'VISIO_STORE',
  TEST_AND_BUY = 'TEST_AND_BUY',
}

export interface BookingShop {
  id: string;
  storeId: string;
  country: string;
  geolocation: {
    latitude: number;
    longitude: number;
  };
  workingHoursSpecification: BookingShopWorkingHours[];
  timeZone: string;
  oneffZoneId: string;
  oneffZoneIdForRemote: string | null;
  attributes?: OneStoreShopAttributes;
  address?: OneStoreAddressAttributesInterface;
  workingHours?: OneStoreWorkingHourAttributesInterface[];
  distance?: number;
  nextAvailableTimeSlots?: {
    start_sec: number;
    duration_sec: number;
    quantity: number;
  }[];
  remoteCountriesAvailable?: string[];
  context: BookingShopContextEnum;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export enum BookingReservationStatusEnum {
  CANCELED_BY_CUSTOMER = 'CANCELED_BY_CUSTOMER',
  VALIDATED = 'VALIDATED',
}

export interface BookingReservation {
  id: string;
  country: string;
  context?: BookingShopContextEnum;
  customerId: string;
  customerEmail: string;
  customerFirstName?: string;
  customerLocale: string;
  shopId: string;
  startTime: string;
  duration: number;
  extraTimeAfterSec: number;
  status: BookingReservationStatusEnum;
  sku: string;
  metadata: {
    userComment: string;
    serviceOptions?: {
      size?: string;
    };
    sharedPhoneNumber?: string;
    sharedOrderNumber?: string;
    sharedLeasingNumber?: string;
  };
  internalNote?: BookingInternalNote;
  resources?: {
    reservationId?: string;
    resourceSku?: string;
    exceptionId?: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string | null;
  }[];
  timeZone: string;
  isNoShow: boolean;
  externalStatus: string | null;
  orderId: string;
  itemId: string;
  storeId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  isCancelable?: boolean;
  meeting?: Meeting;
}

export class ConflictInResourceUpdateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConflictInResourceUpdateError';

    Object.setPrototypeOf(this, ConflictInResourceUpdateError.prototype);
  }
}

export enum BookingReservationCancellationReasonEnum {
  RESOURCE_UNAVAILABLE = 'RESOURCE_UNAVAILABLE',
  CUSTOMER_REQUEST = 'CUSTOMER_REQUEST',
  EQUIPMENT_UNAVAILABLE = 'EQUIPMENT_UNAVAILABLE',
  OTHER = 'OTHER',
}

export interface BookingReservationCancellationPayload {
  releaseAvailability: boolean;
  reasonInformation: string;
  reason: BookingReservationCancellationReasonEnum;
}

export interface BookingReservationResourceUpdatePayload {
  intents: Array<{
    toResourceSku: string;
    fromResourceLinkedToExceptionId: string;
  }>;
  force: boolean;
}

export enum BookingResourceTypeEnum {
  PERSON = 'person',
  RESOURCE = 'resource',
}

export interface BookingResource {
  sku: string;
  name: string;
  shopId: string;
  services?: (BookingService & { category?: CategoryTypeEnum })[];
  scheduleId: string | null;
  schedules?: BookingSchedule[];
  type: BookingResourceTypeEnum;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface BookingResponseError {
  error_code: number;
  error: string;
  error_description: string;
}

export interface BookingOrderItemPayload {
  skuId: string;
  optionId: string;
  quantity: number;
  priceId: string;
  unitPrice: number;
  fulfillerId: string;
  serviceDetails: {
    startSec: number;
    durationSec: number;
  };
}

export enum AppNameEnum {
  BACK_OFFICE = 'back_office',
  WIDGET = 'widget',
}

export interface BookingOrderMetadata {
  fromApp?: AppNameEnum;
  userComment?: string;
  serviceOptions?: {
    size?: string;
  };
  sharedPhoneNumber?: string;
  sharedOrderNumber?: string;
  sharedLeasingNumber?: string;
  codeModel?: string;
  bookedBy?: {
    provider: string;
    uuid: string;
  };
  processId?: string;
  fromOrder?: string;
  resourceSkus?: string[];
}

export interface BookingOrderPayload {
  country: string;
  geolocation: {
    latitude: number;
    longitude: number;
  };
  radius: number;
  oneffZoneIds: string[];
  shopId: string;
  currencyCode: string;
  email: string;
  sub: string;
  firstName?: string;
  locale: string;
  metadata?: BookingOrderMetadata;
  items: BookingOrderItemPayload[];
  resourceIdsFilter?: string[];
  remoteCountriesAvailable?: string[];
}

export interface BookingOrderItem {
  skuId: string;
  itemId: string;
  optionId: string;
  serviceId: string;
  quantity: number;
  exceptionsId: string[];
  serviceDetails: {
    startSec: number;
    durationSec: number;
  };
}

export interface BookingOrder {
  id: string;
  containerId: string;
  shippingId: string;
  customerId: string;
  shopId: string;
  status: string;
  items: BookingOrderItem[];
  meetingInfo?: Meeting;
  merchantId?: string;
  metadata?: BookingOrderMetadata;
}

export type BookingOrderResponse = BookingOrder | BookingResponseError;

export const isBookingOrder = (
  response: BookingOrderResponse,
): response is BookingOrder => {
  return (response as BookingOrder)?.items !== undefined;
};

export const isBookingResponseError = (
  response: unknown,
): response is BookingResponseError => {
  return (response as BookingResponseError).error_code !== undefined;
};

export interface BookingSchedule {
  id: string;
  timeZone: string;
  specification: BookingShopWorkingHours[];
  shopId: string;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  resourceSku: string;
  resources?: BookingResource[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export enum BookingExceptionTypeEnum {
  MANUAL = 'MANUAL',
  SYSTEM = 'SYSTEM',
}

export interface BookingInternalNote {
  content: string;
  updatedBy?: string;
  updatedAt?: Date;
}

export interface BookingException {
  id: string;
  duration: number;
  quantity: number;
  sku: string;
  startTime: string;
  shopId: string;
  type: BookingExceptionTypeEnum;
  internalNote?: BookingInternalNote;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export enum CancelOrderReasonEnum {
  REPLACEMENT = 'REPLACEMENT',
  REFUND = 'REFUND',
}

export interface BookingProductSheetWithPrice extends BookingProductSheet {
  offer?: BookingCountryServiceOffer;
}
